import React, { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { Button, Alert, Spinner } from 'react-bootstrap';
import { supabase } from '../supabase/supabase';
import { ReusableModal, ModalAction } from './ReusableModal';
import { GlobalContext } from '../context/GlobalContext';
import { getCheckoutSession } from '../api';
import { CheckoutElementsProvider, PaymentElement } from '@stripe/react-stripe-js/checkout';
import { loadStripe } from '@stripe/stripe-js';
import CheckoutForm from './Payment/CheckoutForm';

interface Plan {
    id: string;
    name: string;
    price: number;
    duration: number;
    description?: string;
}

interface FormProps {
    show: boolean;
    handleClose: () => void;
    missionaryId: string;
    handleSubmit: (clientSecret: string) => void;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export default function SubscriptionForm({
    show,
    handleClose,
    handleSubmit,
    missionaryId
}: FormProps) {
    const [plans, setPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const global = useContext(GlobalContext);

    useEffect(() => {
        async function fetchPlans() {
            try {
                setLoading(true);
                const { data, error } = await supabase
                    .from('plans')
                    .select('id, name, price, duration, description');
                if (error) throw error;
                setPlans(data || []);
            } catch (err) {
                setError('Failed to load plans');
                console.error('Error fetching plans:', err);
            } finally {
                setLoading(false);
            }
        }

        fetchPlans();
    }, []);

    const createCheckoutSession = async (planId: string) => {
        try {
            setLoading(true);
            setError(null);

            const { clientSecret } = await getCheckoutSession(planId, missionaryId);

            handleSubmit(clientSecret);
            handleClose();
        } catch (err) {
            const message =
                err instanceof Error ? err.message : 'Failed to create checkout session';
            setError(message);
            console.error('Error creating checkout session:', err);
            setLoading(false);
        }
    };

    if (loading && plans.length === 0) {
        return (
            <div className="stripe-loading">
                <Spinner animation="border" variant="primary" />
                <p>Loading plans...</p>
            </div>
        );
    }

    const actions: ModalAction[] = [
        {
            label: 'Cancel',
            callback: handleClose,
            active: true
        }
    ];

    return (
        <ReusableModal
            title="Pick your plan"
            show={show}
            actions={actions}
            handleClose={handleClose}
        >
            {/* <div className="stripe-form"> */}
            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            <div className="plans-selection">
                {/* Plan selection */}

                <div className="plans-grid">
                    {plans.map((plan) => (
                        <div key={plan.id} className="plan-option">
                            <div className="plan-header">
                                <h4 className="plan-name">{plan.name}</h4>
                                <div className="plan-price">
                                    <span className="price-amount">${plan.price}</span>
                                    <span className="price-period">
                                        /{plan.duration} month{plan.duration > 1 ? 's' : ''}
                                    </span>
                                </div>
                            </div>
                            {plan.description && (
                                <p className="plan-description">{plan.description}</p>
                            )}
                            <Button
                                onClick={() => createCheckoutSession(plan.id)}
                                disabled={loading}
                                className="select-plan-btn"
                                size="lg"
                            >
                                Select Plan
                            </Button>
                        </div>
                    ))}
                </div>
            </div>
            {/* )} */}
            {/* </div> */}
        </ReusableModal>
    );
}
